import { h, Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { useThreekit } from './threekit-context';
import Drawer from './components/drawer';
import DrawerButtonGroup from './components/drawer-button-group';
import ExitModal from './components/modals/exit-modal';
import {
  setBladeView,
  setExitBladeView,
  setMobileAndDesktopView,
  set2DMobileAndDesktopView,
} from './components/util/setCameras';
import { useDrawer } from './drawer-context';
import ReviewYourKnifeModal from './components/modals/review-your-knife-modal';
import SocialShareModal from './components/modals/social-share-modal';
import OrientationWarningModal from './components/modals/orientation-warning-modal';
import setNodeNameChange from './components/util/setNodeNameChange';

function ProductCustomizer() {
  const {
    products,
    configurator,
    player,
    bladeSide,
    setBladeSide,
    configuration,
    setKnifeClickActive,
    knifeClickActive,
    productTag,
  } = useThreekit();
  const { drawerName, drawerCategory, openDrawer, setDrawerCategory } =
    useDrawer();
  const previousBladeSideState = useRef({ bladeSide: 'Front' });
  const previousDrawerCategoryState = useRef({
    drawerCategory: 'Color & Material',
  });

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      set2DMobileAndDesktopView(configurator);
      if (drawerName === 'Blade' && window.innerWidth < 1024) {
        setBladeView(configurator, player);
        return;
      }

      setExitBladeView(configurator);
      setTimeout(
        setMobileAndDesktopView(configurator, player, bladeSide),
        1000,
      );
    };

    if (configurator && player) {
      set2DMobileAndDesktopView(configurator);
      if (drawerName === 'Blade' && window.innerWidth < 1024) {
        setBladeView(configurator, player, bladeSide);
      } else {
        setMobileAndDesktopView(
          configurator,
          player,
          bladeSide,
          previousBladeSideState.current.bladeSide,
          drawerCategory,
          previousDrawerCategoryState.current.drawerCategory,
        );
        if (window.innerWidth < 1024 && drawerName !== 'Hardware') {
          setExitBladeView(configurator);
        }
      }
      window.addEventListener('resize', handleResize);
      if (previousBladeSideState.current.bladeSide !== bladeSide) {
        previousBladeSideState.current.bladeSide = bladeSide;
      }
      if (
        previousDrawerCategoryState.current.drawerCategory !== drawerCategory
      ) {
        previousDrawerCategoryState.current.drawerCategory = drawerCategory;
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [configurator, player, drawerName, drawerCategory, bladeSide]);

  useEffect(() => {
    const initPlayerSelection = () => {
      // Helps with click events on the player
      player.tools.addTool({
        key: 'partSelect',
        label: 'Part Select Tool',
        active: true,
        enabled: true,
        handlers: {
          click: (ev) => {
            const hits = ev.hitNodes;
            const nodeId = hits && hits.length > 0 && hits[0].nodeId;
            if (nodeId) {
              const selectable = player.scene.get({
                id: nodeId,
                plug: 'Properties',
                property: 'selectable',
              });
              if (selectable || typeof selectable === 'undefined') {
                setKnifeClickActive(true);
                player.selectionSet.set(nodeId);
                const nodeInfo = player.scene.get({ id: nodeId });
                const nodeName = nodeInfo.name;
                setNodeNameChange(
                  nodeName.toLowerCase().replaceAll('-', '_'),
                  openDrawer,
                  setBladeSide,
                  setDrawerCategory,
                );
              }
            } else {
              player.selectionSet.clear();
              setKnifeClickActive(false);
            }
          },
        },
      });
    };

    if (player && player.tools) {
      if (!knifeClickActive) {
        player.selectionSet.clear();
      }
      if (
        productTag !== 'cstm:personalization' &&
        productTag !== 'cstm:service'
      ) {
        // Need timeout for camera events to fire
        setTimeout(() => initPlayerSelection(), 2500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configuration,
    drawerName,
    drawerCategory,
    player,
    setBladeSide,
    bladeSide,
    openDrawer,
    knifeClickActive,
  ]);

  if (!products) {
    return (
      <div className="absolute top-0 left-0 w-screen h-screen bg-bg-1/[0.3]" />
    );
  }

  return (
    <>
      <SocialShareModal />
      <ReviewYourKnifeModal />
      <ExitModal />
      <OrientationWarningModal />
      <DrawerButtonGroup />
      <Drawer />
    </>
  );
}

export default ProductCustomizer;
